import React from 'react';
import { LucideIcon } from 'lucide-react';

/**
 * The single button API for the whole app. Enforces the house rule — every action is an
 * icon + a short label — and centralizes variants so they can't drift.
 *
 * Renders the existing .mx button classes (admin chrome) by default; tone="paper" swaps to the
 * reviewer (.dh) skin so the two registers stay visually distinct but structurally identical.
 */
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
type Size = 'sm' | 'md';

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: LucideIcon;
  variant?: Variant;
  size?: Size;
  tone?: 'admin' | 'paper';
  loading?: boolean;
}

const ICON_SIZE: Record<Size, number> = { sm: 14, md: 15 };

// .mx (admin) variant → class
const MX: Record<Variant, string> = {
  primary: 'mx-btn-primary', secondary: 'mx-btn-outline', ghost: 'mx-btn-ghost',
  danger: 'mx-btn-danger', success: 'mx-btn-primary',
};
// .dh (paper/reviewer) variant → class
const DH: Partial<Record<Variant, string>> = {
  primary: 'dh-btn p', secondary: 'dh-btn', ghost: 'dh-btn', danger: 'dh-btn', success: 'dh-btn p',
};

const Button: React.FC<Props> = ({
  icon: Icon, variant = 'secondary', size = 'md', tone = 'admin', loading = false,
  children, className = '', disabled, ...rest
}) => {
  const cls = tone === 'paper'
    ? `dh-btn ${DH[variant] || ''}`
    : `mx-btn ${MX[variant]} ${size === 'sm' ? 'mx-btn-sm' : ''}`;
  return (
    <button className={`${cls} ${className}`.trim()} disabled={disabled || loading} {...rest}>
      {Icon && <Icon size={ICON_SIZE[size]} strokeWidth={2} className={loading ? 'mx-spin' : undefined} />}
      {children}
    </button>
  );
};

/**
 * Icon-only action with a required tooltip (for dense rows where a label won't fit).
 * The label still exists — as the tooltip and aria-label — so nothing becomes a mystery glyph.
 */
export const IconButton: React.FC<Omit<Props, 'children'> & { label: string }> = ({
  icon: Icon, label, variant = 'ghost', size = 'sm', tone = 'admin', className = '', disabled, loading, ...rest
}) => {
  const cls = tone === 'paper' ? 'dh-btn' : `mx-btn ${MX[variant]} mx-btn-sm`;
  return (
    <button className={`${cls} ${className}`.trim()} aria-label={label} title={label}
      disabled={disabled || loading} {...rest}>
      {Icon && <Icon size={ICON_SIZE[size]} strokeWidth={2} className={loading ? 'mx-spin' : undefined} />}
    </button>
  );
};

export default Button;
